import React from 'react';
import styled from '@grebban/style-system-react';
import Button from 'components/Button';
import type { ThemeName } from 'config/branding/themes';
import { useHeaderNavigationContext } from '@fogia/core-web/contexts/HeaderNavigationContext';
import { type DesktopNavMenuItem } from 'components/Header/types';

const MenuList = styled('nav')`
    display: flex;
    align-items: flex-start;
    gap: 30px;
    flex-wrap: nowrap;
`;

const MenuItem = styled(Button)`
    white-space: nowrap;
`;

export type { DesktopNavMenuItem };
export type DesktopNavMenuPlacement = 'left' | 'right';

export interface DesktopNavMenuProps {
    items: DesktopNavMenuItem[] | null | undefined;
    /** e.g. "Main menu" or "Utility menu" */
    ariaLabel: string;
    /** Which side this menu sits on; used for overlay alignment (open from left vs right). */
    placement: DesktopNavMenuPlacement;
    /** data-theme for the menu (scoped to MenuList so only these buttons use it). Defaults to "light". */
    dataTheme?: ThemeName;
}

const hasSubmenu = (item: DesktopNavMenuItem): boolean => !!(item.content?.submenu || item.links?.length);

export const DesktopNavMenu = ({ items, ariaLabel, placement, dataTheme = 'light' }: DesktopNavMenuProps) => {
    const { headerNavigationState, setHeaderNavigationState } = useHeaderNavigationContext();
    const list = Array.isArray(items) && items.length > 0 ? items : [];
    if (list.length === 0) return null;

    const menuOpen = !!headerNavigationState.activeSubMenu;

    const handleClick = (label: string, hasSub: boolean) => {
        if (!hasSub) return;
        if (headerNavigationState.activeSubMenu === label) {
            setHeaderNavigationState({
                ...headerNavigationState,
                activeSubMenu: '',
                desktopMenuActive: false,
                /* Keep desktopMenuAnchor so panel stays on correct side during fade-out */
            });
        } else {
            setHeaderNavigationState({
                ...headerNavigationState,
                activeSubMenu: label,
                desktopMenuActive: true,
                desktopMenuAnchor: placement,
            });
        }
    };

    return (
        <MenuList role="navigation" aria-label={ariaLabel} data-theme={dataTheme}>
            {list.map((item, index) => {
                const label = item.label ?? item.link?.text ?? '';
                const hasSub = hasSubmenu(item);
                const href = item.link?.url ?? '/';
                const isActive = headerNavigationState.activeSubMenu === label;
                const key = label ? `${label}-${index}` : `nav-${index}`;

                if (hasSub) {
                    return (
                        <MenuItem
                            key={key}
                            color={isActive ? 'var(--button-tertiery-text-active)' : ''}
                            theme="textButton"
                            typography="Primary/15_120_510_-1"
                            type="button"
                            aria-label={label}
                            onClick={() => handleClick(label, hasSub)}
                            aria-expanded={menuOpen && isActive}
                            aria-haspopup="true"
                        >
                            {label}
                        </MenuItem>
                    );
                }
                return (
                    <MenuItem
                        key={key}
                        theme="textButton"
                        typography="Primary/15_120_510_-1"
                        url={href}
                        aria-label={label}
                    >
                        {label}
                    </MenuItem>
                );
            })}
        </MenuList>
    );
};
